import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { handHoldsApi } from '../services/api';
import { HandHold, CreateHandHoldData } from '../types';

const HandHoldsManagement: React.FC = () => {
  const [showForm, setShowForm] = useState(false);
  const [editingHandHold, setEditingHandHold] = useState<HandHold | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const queryClient = useQueryClient();

  const { data: handHoldsData, isLoading } = useQuery({
    queryKey: ['handHolds', { page, search }],
    queryFn: () => handHoldsApi.getAll({ page, limit: 10, search: search || undefined }),
  });

  const { register, handleSubmit, reset, formState: { errors } } = useForm<CreateHandHoldData>();

  const createMutation = useMutation({
    mutationFn: handHoldsApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['handHolds'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      reset();
      setShowForm(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: CreateHandHoldData }) =>
      handHoldsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['handHolds'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      reset();
      setEditingHandHold(null);
      setShowForm(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: handHoldsApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['handHolds'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
    },
  });

  const onSubmit = (data: CreateHandHoldData) => {
    if (editingHandHold) {
      updateMutation.mutate({ id: editingHandHold.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleEdit = (handHold: HandHold) => {
    setEditingHandHold(handHold);
    reset({
      name: handHold.name,
      description: handHold.description || '',
    });
    setShowForm(true);
  };

  const handleDelete = (handHold: HandHold) => {
    if (window.confirm(`Are you sure you want to delete "${handHold.name}"?`)) {
      deleteMutation.mutate(handHold.id);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingHandHold(null);
    reset();
  };

  const handHolds = handHoldsData?.data || [];
  const pagination = handHoldsData?.pagination;

  if (isLoading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="hand-holds-management">
      <div className="page-header">
        <h1>Hand Holds Management</h1>
        <button
          className="btn btn-primary"
          onClick={() => setShowForm(true)}
          disabled={showForm}
        >
          Add New Hand Hold
        </button>
      </div>

      {showForm && (
        <div className="form-container">
          <h2>{editingHandHold ? 'Edit Hand Hold' : 'Create New Hand Hold'}</h2>
          <form onSubmit={handleSubmit(onSubmit)} className="admin-form">
            <div className="form-group">
              <label htmlFor="name">Name *</label>
              <input
                {...register('name', { required: 'Name is required' })}
                id="name"
                type="text"
                placeholder="e.g., Left - Right"
                className={errors.name ? 'error' : ''}
              />
              {errors.name && <span className="error-text">{errors.name.message}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                {...register('description')}
                id="description"
                rows={3}
                placeholder="Describe the hand hold position and technique"
              />
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary">
                {editingHandHold ? 'Update' : 'Create'} Hand Hold
              </button>
              <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="table-controls">
        <input
          type="text"
          placeholder="Search hand holds..."
          aria-label="Search hand holds"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="search-input"
        />
      </div>

      {isLoading ? (
        <div className="loading">Loading hand holds...</div>
      ) : handHolds.length > 0 ? (
        <>
          <div className="table-container">
            <table className="admin-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Description</th>
              <th>Moves Count</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {handHolds.map((handHold) => (
              <tr key={handHold.id}>
                <td>
                  <strong>{handHold.name}</strong>
                </td>
                <td>{handHold.description || '-'}</td>
                <td>{handHold.movesCount || 0}</td>
                <td>{new Date(handHold.createdAt).toLocaleDateString()}</td>
                <td>
                  <div className="action-buttons">
                    <button
                      className="btn btn-small btn-secondary"
                      onClick={() => handleEdit(handHold)}
                    >
                      Edit
                    </button>
                    <button
                      className="btn btn-small btn-danger"
                      onClick={() => handleDelete(handHold)}
                      disabled={deleteMutation.isPending}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            </tbody>
          </table>
        </div>

        {pagination && pagination.pages > 1 && (
          <div className="pagination">
            {Array.from({ length: pagination.pages }, (_, i) => i + 1).map(
              (pageNum) => (
                <button
                  key={pageNum}
                  className={`btn btn-small ${
                    pageNum === page ? 'btn-primary' : 'btn-secondary'
                  }`}
                  onClick={() => setPage(pageNum)}
                >
                  {pageNum}
                </button>
              )
            )}
          </div>
        )}
      </>
      ) : (
        <div className="empty-state">
          <p>No hand holds found. Create your first hand hold!</p>
        </div>
      )}
    </div>
  );
};

export default HandHoldsManagement;